import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Select, Button, Card, Spin, Row, Col, Input, DatePicker, InputNumber, Typography } from 'antd';
import {
  getSpaceActivities,
  getSpaceCategoryDetails,
  filterSpaceName,
  getSpaceData,
  getSpacesByFilter,
  fetchSpaceDetails
} from '../../Dashboard/Spaces/SpaceAction';
import ListSuperDeals from './ListSuperDeals/ListSuperDeals';
import { createSuperDeal } from './AddSuperDealAction';

const { Option } = Select;
const { Text, Title } = Typography;

const AddSuperDeal = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [countryCode, setCountryCode] = useState('LK'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchedSpaceDetails, setFetchedSpaceDetails] = useState(false);
  const [selectedChargingType, setSelectedChargingType] = useState(null);
  const [selectedChargingTypeDetails, setSelectedChargingTypeDetails] = useState(null);
  const [loading, setLoading] = useState({
    categories: false,
    activities: false,
    spaces: false,
    submitting: false
  });

  const spaceActivities = useSelector((state) => state.space?.spaceCategory?.data || []);
  const filteredSpacesByFilter = useSelector((state) => state.space?.spacesByFilter?.data || []);
  const spaceData = useSelector((state) => state.space?.spaceData?.data?.content || []);
  const spaceDetails = useSelector((state) => state.space?.spaceDetails);

  const [chargingOptions, setChargingOptions] = useState([]);
  const [chargingLoading, setChargingLoading] = useState(false);
  const [spacesReady, setSpacesReady] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const checkFormValidation = async () => {
      try {
        await form.validateFields();
        setHasErrors(false);
      } catch (errorInfo) {
        setHasErrors(true);
      }
    };

    if (selectedCategory && selectedActivityId && availableSpaces.length > 0 && selectedChargingType) {
      checkFormValidation();
    } else {
      setHasErrors(false);
    }
  }, [form, selectedCategory, selectedActivityId, availableSpaces.length, selectedChargingType]);

  useEffect(() => {
    setLoading(prev => ({ ...prev, categories: true }));
    dispatch(getSpaceActivities());
    dispatch(getSpaceCategoryDetails(countryCode));
  }, [dispatch, countryCode]);

  const handleSpaceSelect = (spaceId) => {
    setSelectedChargingType(null);
    setSelectedChargingTypeDetails(null);
    form.setFieldsValue({ chargingType: null, newPrice: null });

    setChargingOptions([]);
    setChargingLoading(true);
    setFetchedSpaceDetails(false);

    dispatch(fetchSpaceDetails(spaceId, selectedCategory, setFetchedSpaceDetails));
    form.setFieldsValue({ spaceId });
  };

  const getChargingTypes = () => {
    if (spaceDetails?.data?.chargings && Array.isArray(spaceDetails.data.chargings)) {
      return spaceDetails.data.chargings;
    }
    if (spaceDetails?.data?.events?.[0]?.chargings) {
      return spaceDetails.data.events[0].chargings;
    }
    return [];
  };

  useEffect(() => {
   if (fetchedSpaceDetails && spaceDetails) {
     const types = getChargingTypes() || [];
    setChargingOptions(types);
     setChargingLoading(false);
   } else {
    setChargingOptions([]);
    setChargingLoading(false);
   }
}, [fetchedSpaceDetails, spaceDetails]); 

  const handleChargingTypeSelect = (chargingTypeId) => {
    const chargingTypes = getChargingTypes();
    const selectedCharging = chargingTypes.find(charging => charging.chargingType.id === chargingTypeId);
    
    if (selectedCharging) {
      setSelectedChargingType(chargingTypeId);
      setSelectedChargingTypeDetails(selectedCharging);
      form.setFieldsValue({ chargingType: chargingTypeId });
    }
  };

  const formatPricingOptions = (pricingOptions) => {
    if (!pricingOptions || pricingOptions.length === 0) {
      return 'No pricing available';
    }

    return pricingOptions.map(option => {
      const timeRange = option.startTime && option.endTime 
        ? `${option.startTime} - ${option.endTime}` 
        : 'All day';
      
      return `${option.name}: ${option.price.currencySymbol} ${option.price.amount} (${timeRange})`;
    }).join(', ');
  };

  const handleCategoryChange = (categoryId) => {
    const selectedCat = spaceActivities.find(cat => cat.id === categoryId);
    
    if (selectedCat) {
      setSelectedCategory(selectedCat.name);
      setVenueTypes(selectedCat.venueType || []);
      
      setSelectedActivityId(null);
      setAvailableSpaces([]);
      setSelectedChargingType(null);
      setSelectedChargingTypeDetails(null);
      setChargingOptions([]);
      setChargingLoading(false);
      setFetchedSpaceDetails(false);
      form.setFieldsValue({
        activityId: null,
        spaceId: null,
        chargingType: null,
        newPrice: null
      });

      form.setFieldsValue({ 
        eventType: {
          spaceCategory: {
            id: categoryId
          }
        }
      });
    }
  };

  const handleActivityChange = (activityId) => {
    setSelectedActivityId(activityId);
    
    setAvailableSpaces([]);
    setSpacesReady(false);
    setSelectedChargingType(null);
    setSelectedChargingTypeDetails(null);
    setChargingOptions([]);          
    setChargingLoading(false);       
    setFetchedSpaceDetails(false); 
    form.setFieldsValue({ 
      spaceId: null,
      chargingType: null,
      newPrice: null
    });

    if (activityId) {
      setLoading(prev => ({ ...prev, spaces: true }));
      fetchSpacesByActivity(activityId); 
    }
  };

  const fetchSpacesByActivity = async (activityId) => {
    try {
      setLoading(prev => ({ ...prev, spaces: true }));
      setSpacesReady(false);
      
      const venue = venueTypes.find(v => v.id === activityId);
      const eventType = venue ? venue.code : null;
      
      if (eventType && selectedCategory) {
        await dispatch(getSpacesByFilter(selectedCategory, eventType, countryCode));
      }
      
    } catch (error) {
      console.error('Error fetching spaces:', error);
      setAvailableSpaces([]);
      setLoading(prev => ({ ...prev, spaces: false }));
    }
  };

  // useEffect(() => {
  //   if (filteredSpacesByFilter && filteredSpacesByFilter.length > 0) {
  //     setAvailableSpaces(filteredSpacesByFilter);
  //     setLoading(prev => ({ ...prev, spaces: false }));
  //   } else if (selectedActivityId) { 
  //     setAvailableSpaces([]);
  //     setLoading(prev => ({ ...prev, spaces: false }));
  //   }
  // }, [filteredSpacesByFilter, selectedActivityId]); 

  useEffect(() => {
   if (filteredSpacesByFilter && filteredSpacesByFilter.length > 0) {
     
     setAvailableSpaces(filteredSpacesByFilter);
     setSpacesReady(true);
     setLoading(prev => ({ ...prev, spaces: false }));
   } else if (selectedActivityId) {
     setAvailableSpaces([]);
     setSpacesReady(false);
     setLoading(prev => ({ ...prev, spaces: false }));
   }
 }, [filteredSpacesByFilter, selectedActivityId]); 

  // Handle space search
  const handleSpaceSearch = (value) => {
    if (value && value.length > 2 && availableSpaces.length > 0) {
      const searchedSpaces = availableSpaces.filter(space =>
        space.name?.toLowerCase().includes(value.toLowerCase()) ||
        space.location?.address?.toLowerCase().includes(value.toLowerCase())
      );
      setAvailableSpaces(searchedSpaces);
    } else if (!value) {
      if (filteredSpacesByFilter && filteredSpacesByFilter.length > 0) {
        setAvailableSpaces(filteredSpacesByFilter);
      }
    }
  };

  const getVenueName = (activityId) => {
    if (activityId && venueTypes.length > 0) {
      const venue = venueTypes.find(venue => venue.id === activityId);
      return venue ? venue.code || venue.name : '';
    }
    return '';
  };

  const onFinish = async (values) => {
    setLoading(prev => ({ ...prev, submitting: true }));
    
    // try {
      const superDealData = {
        // category: selectedCategory,
        // activityId: selectedActivityId, 
        spaceId: values.spaceId,
        chargingType: selectedChargingType,
        // chargingTypeName: selectedChargingTypeDetails.chargingType.name,
        discountedValue: values.newPrice,
        // originalPrice: selectedChargingTypeDetails?.pricingOptions?.[0]?.price?.amount,
        // currency: selectedChargingTypeDetails?.pricingOptions?.[0]?.price?.currencySymbol,
        // country: countryCode,
        // eventType: selectedActivityId ? (() => {
        //   const venue = venueTypes.find(v => v.id === selectedActivityId);
        //   return venue ? venue.code : null;
        // })() : null,
        // ...values
      };

      dispatch(createSuperDeal(
    superDealData,
    (data) => {
      // alert('Super Deal created!');
      resetSuperDealForm();
      setLoading(prev => ({ ...prev, submitting: false }));
    },
    (error) => {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  ));
  };

  const resetSuperDealForm = () => {
    form.resetFields();
    setSelectedCategory(null);
    setSelectedActivityId(null); 
    setAvailableSpaces([]);
    setVenueTypes([]);
    setSearchTerm('');
    setSelectedChargingType(null);
    setSelectedChargingTypeDetails(null);
    setChargingOptions([]);      
    setChargingLoading(false);    
    setFetchedSpaceDetails(false); 
    setSpacesReady(false);         
    setHasErrors(false);
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
  };

  return (
    <div className="super-deal-add">
      <Card title="Add Super Deal" className="super-deal-card">
        {loading.categories && spaceActivities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Loading categories..." />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={() => {
              setTimeout(() => {
                form.validateFields()
                  .then(() => setHasErrors(false))
                  .catch(() => setHasErrors(true));
              }, 100);
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Category"
                  name="categoryId"
                  rules={[{ required: true, message: 'Please select a category!' }]}
                >
                  <Select
                    placeholder="Select Category"
                    onChange={handleCategoryChange}
                  >
                    {spaceActivities.map(category => (
                      <Option key={category.id} value={category.id}>
                        {category.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Activity Type"
                  name="activityId" 
                  rules={[{ required: true, message: 'Please select an activity type!' }]}
                >
                  <Select
                    placeholder="Select Activity Type"
                    onChange={handleActivityChange}
                    disabled={!selectedCategory}
                  >
                    {venueTypes.map(venue => (
                      <Option key={venue.id} value={venue.id}>
                        {venue.name || venue.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
            {selectedActivityId && spacesReady && (
              <Col span={12}>
                <Form.Item
                  label="Space"
                  name="spaceId"
                  rules={[{ required: true, message: 'Please select a space!' }]}
                >
                  <Select
                    placeholder={
                      !selectedActivityId 
                        ? "Please select activity type first" 
                        : loading.spaces 
                        ? "Loading spaces..." 
                        : "Select Space"
                    }
                    disabled={!selectedActivityId || loading.spaces} 
                    loading={loading.spaces}
                    showSearch
                    onSearch={handleSpaceSearch}
                    onSelect={handleSpaceSelect}
                    filterOption={false}
                    notFoundContent={
                      loading.spaces ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <Spin size="small" />
                          <div>Loading spaces...</div>
                        </div>
                      ) : (
                        "No spaces found"
                      )
                    }
                  >
                    {/* {availableSpaces.map(space => (
                      <Option key={space.id} value={space.id}>
                        <div>
                          <strong>{space.name}</strong>
                          <br />
                          <small>{space.location?.address || 'No address'}</small>
                          <br />
                        </div>
                      </Option>
                    ))} */}
                    {Array.isArray(availableSpaces) && availableSpaces.map(space => (
                      <Option key={space.id} value={space.id}>
                        <div>
                         <strong>{space.name}</strong>
                         <br />
                          <small>{space.location?.address || 'No address'}</small>
                     </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              )}
              
              {chargingOptions.length > 0 && (
                    <Col span={12}>
                      <Form.Item
                        label="Charging Type"
                        name="chargingType"
                        rules={[{ required: true, message: 'Please select a charging type!' }]}
                      >
                        <Select
                           placeholder="Select Charging Type"
                        onChange={handleChargingTypeSelect}
                         disabled={chargingLoading || chargingOptions.length === 0}
                          notFoundContent={chargingLoading ? <Spin size="small" /> : null}
                         >
                          {chargingOptions.map(charging => (
                            <Option key={charging.chargingType.id} value={charging.chargingType.id}>
                              <div>
                                <strong>{charging.chargingType.name}</strong>
                                <br />
                                <small>{charging.chargingType.description}</small>
                              </div>
                           </Option>
                         ))}
                        </Select>
                      </Form.Item>
                    </Col>
                 )} 
            </Row>

                {selectedChargingTypeDetails && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Card title="Set New Price" style={{ marginBottom: 16 }} size="small">
                        <Row gutter={16}>
                            <Col span={12}>
                              <Text strong>Current Pricing for {selectedChargingTypeDetails.chargingType.name}:</Text>
                              <div style={{ marginTop: 8, marginBottom: 16 }}>
                                {selectedChargingTypeDetails.pricingOptions?.map((option, index) => (
                                  <div key={index} style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #d9d9d9', 
                                    borderRadius: '4px', 
                                    marginBottom: '4px',
                                    backgroundColor: '#f0f0f0'
                                  }}>
                                    <Text>{option.name}: <strong>{option.price.currencySymbol} {option.price.amount}</strong></Text>
                                    {option.startTime && option.endTime && (
                                      <Text type="secondary"> ({option.startTime} - {option.endTime})</Text>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                name="newPrice"
                                label="New Super Deal Price"
                                rules={[
                                  { required: true, message: 'Please enter the new price' },
                                  { type: 'number', min: 0.01, message: 'Price must be greater than 0' },
                                  {
                                    validator: (_, value) => {
                                      if (!value || !selectedChargingTypeDetails?.pricingOptions?.length) {
                                        return Promise.resolve();
                                      }
                                      
                                      const currentPrices = selectedChargingTypeDetails.pricingOptions.map(
                                        option => option.price.amount
                                      );
                                      const minCurrentPrice = Math.min(...currentPrices);
                                      
                                      if (value >= minCurrentPrice) {
                                        return Promise.reject(
                                          new Error(`New price must be less than current price (${selectedChargingTypeDetails.pricingOptions[0]?.price?.currencySymbol} ${minCurrentPrice})`)
                                        );
                                      }
                                      
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                                style={{ marginTop: 8 }}
                              >
                                <InputNumber
                                  style={{ width: '100%' }}
                                  min={0.01}
                                  precision={2}
                                  placeholder="Enter new price"
                                  addonBefore={selectedChargingTypeDetails.pricingOptions?.[0]?.price?.currencySymbol || 'LKR'}
                                />
                              </Form.Item>
                            </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                )}

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading.submitting}
                disabled={!selectedCategory || !selectedActivityId || availableSpaces.length === 0 || !selectedChargingType || hasErrors   }
              >
                Create Super Deal
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
      <ListSuperDeals />
    </div>
  );
};

export default AddSuperDeal;